import React, { useState, useEffect, useRef } from "react";
import "../MyAuctions/MyAuctions.css";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  Eye,
  Play,
  Search,
  RefreshCw,
  Gavel,
} from "lucide-react";
import { format, parse, isValid } from "date-fns";
import apiService from "../../../services/apiAuctionService";
import { BaseAuction } from "../../../types/auction";
import { useAuth } from "../../../contexts/AuthContext";
import { API_BASE_URL } from "../../../services/apiConfig";

// Runs `check` over `items` in fixed-size concurrent batches instead of one
// at a time, keeping items in their original order. Used for the per-auction
// "is this user actually invited?" API check so a whole page of auctions
// doesn't wait on N sequential network round trips.
const FILTER_BATCH_SIZE = 6;
async function filterConcurrently<T>(
  items: T[],
  check: (item: T) => Promise<boolean>
): Promise<T[]> {
  const kept: T[] = [];
  for (let i = 0; i < items.length; i += FILTER_BATCH_SIZE) {
    const batch = items.slice(i, i + FILTER_BATCH_SIZE);
    const flags = await Promise.all(batch.map(check));
    batch.forEach((item, idx) => {
      if (flags[idx]) kept.push(item);
    });
  }
  return kept;
}

const MyParticipatedAuctions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [auctions, setAuctions] = useState<BaseAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [participantCounts, setParticipantCounts] = useState<{
    [key: string]: number;
  }>({});
  const [now, setNow] = useState<number>(Date.now());

  const firstLoadRef = useRef(true);

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Fetch participants by auction ID
  const fetchParticipantsByAuctionId = async (auctionId: string | number) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      if (!token) return null;

      const res = await fetch(
        `${API_BASE_URL}/auction/${auctionId}/participants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error(
        `Error fetching participants for auction ${auctionId}:`,
        err
      );
      return null;
    }
  };

  // Check if user created the auction
  const isCreatedByUser = (auction: BaseAuction) => {
    if (!user) return false;

    return (
      auction.createdBy === user.id ||
      auction.userId === user.id ||
      auction.createdBy === user.companyName ||
      auction.auctioneerCompany === user.companyName ||
      (auction as any).auctioneerPhone === user.phoneNumber
    );
  };

  // Check if user is participant via API
  const isUserParticipantViaAPI = async (
    auction: BaseAuction
  ): Promise<boolean> => {
    if (!user) return false;

    const auctionId = auction.backendId || auction.id;
    if (!auctionId) return false;

    try {
      const participantsData = await fetchParticipantsByAuctionId(auctionId);
      if (!participantsData || !participantsData.participants) return false;

      const userPhoneVariations = [
        user.phoneNumber,
        user.phoneNumber?.replace("+91", ""),
        user.phoneNumber?.replace("+", ""),
        "+91" + user.phoneNumber?.replace(/^\+?91?/, ""),
        user.phoneNumber?.replace(/^(\+91|91)/, ""),
      ].filter(Boolean);

      return participantsData.participants.some((participant: any) => {
        if (!participant) return false;

        if (typeof participant === "string") {
          const cleanParticipant = participant.trim();
          return userPhoneVariations.includes(cleanParticipant);
        }

        if (typeof participant === "object") {
          const phoneFields = [
            participant.phone_number,
            participant.phoneNumber,
            participant.phone,
            participant.user_phone,
            participant.contact_number,
          ].filter(Boolean);

          const hasPhoneMatch = phoneFields.some((phone) =>
            userPhoneVariations.includes(phone)
          );

          const userIdFields = [
            participant.user_id,
            participant.userId,
            participant.id,
          ].filter(Boolean);

          const hasUserIdMatch = userIdFields.includes(user.id);

          const emailFields = [
            participant.email,
            participant.mail_id,
            participant.mailId,
          ].filter(Boolean);

          const hasEmailMatch = user.email
            ? emailFields.includes(user.email)
            : false;

          return hasPhoneMatch || hasUserIdMatch || hasEmailMatch;
        }

        return false;
      });
    } catch (error) {
      console.error(
        `Error in API participant check for auction ${auctionId}:`,
        error
      );
      return false;
    }
  };

  // Fetch participated auctions
  const fetchAuctions = async (signal?: AbortSignal) => {
    try {
      let data: BaseAuction[] = [];


      // Filtering function with API checks
      const shouldShowInParticipantTab = async (
        auction: BaseAuction
      ): Promise<boolean> => {
        // CRITICAL: Never show auctions created by the user
        if (isCreatedByUser(auction)) {
          return false;
        }



        // If NOT open to all (invitation only), check if user is explicitly invited
        const isExplicitParticipant = await isUserParticipantViaAPI(auction);

        if (isExplicitParticipant) {
        } else {
        }

        return isExplicitParticipant;
      };

      // Fetch from filtered endpoint
      try {
        const params = {
          status: statusFilter === "all" ? undefined : statusFilter,
          search: debouncedSearch,
          signal,
        };

        data = await apiService.fetchFilteredAuctions(params);

        // Filter auctions using async checks. This used to await one
        // auction at a time (a full network round trip per auction, in
        // series), so a list of 20 auctions meant 20 sequential requests
        // before anything rendered. Running each batch concurrently keeps
        // the exact same per-auction check but no longer waits on it
        // one-by-one, which is the main reason this page felt slow.
        data = await filterConcurrently(data, shouldShowInParticipantTab);
      } catch (filteredErr) {
        console.warn(
          "[ParticipatedAuctions] Filtered endpoint failed:",
          filteredErr
        );
        data = [];
      }

      // Fallback to my-auctions endpoint
      if (data.length === 0) {
        try {
          const myAuctions = await apiService.fetchMyAuctions(
            statusFilter === "all" ? undefined : statusFilter,
            signal
          );

          if (myAuctions.length > 0) {
            data = await filterConcurrently(myAuctions, shouldShowInParticipantTab);
          }
        } catch (myAuctionsErr) {
          console.warn(
            "[ParticipatedAuctions] Fallback failed:",
            myAuctionsErr
          );
        }
      }


      // Double-check: remove any user-created auctions
      const createdByUserCount = data.filter(isCreatedByUser).length;
      if (createdByUserCount > 0) {
        console.warn(
          `[ParticipatedAuctions] ⚠️ Found ${createdByUserCount} user-created auctions! Removing them...`
        );
        data = data.filter((auction) => !isCreatedByUser(auction));
      }

      setAuctions(data);
    } catch (err: any) {
      // Ignore abort errors - they're expected when requests are cancelled
      if (err?.name === 'AbortError') return;
      
      console.error("[ParticipatedAuctions] fetchAuctions failed:", err);
      setApiError(err?.message || "Failed to load auctions");
      setAuctions([]);
    }
  };

  // Initial load and refresh on filter changes
  useEffect(() => {
    if (!user) return;
    setApiError(null);
    const controller = new AbortController();
    const immediate = firstLoadRef.current;
    if (immediate) setLoading(true);
    else setIsFetching(true);

    fetchAuctions(controller.signal).finally(() => {
      if (!controller.signal.aborted) {
        if (immediate) {
          setLoading(false);
          firstLoadRef.current = false;
        } else {
          setIsFetching(false);
        }
      }
    });

    return () => controller.abort();
  }, [statusFilter, debouncedSearch, user]);

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setIsFetching(true);
    try {
      await fetchAuctions();
    } finally {
      setIsFetching(false);
    }
  };

  // Timer for countdowns and status monitoring
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Helper functions for auction timing
  const getAuctionStart = (auction: BaseAuction) => {
    try {
      // Handle both ISO date format and YYYY-MM-DD format from backend
      if (auction.auctionDate.includes('T')) {
        // ISO format: "2025-10-06T18:30:00.000Z" - extract date part and ignore timezone
        const dateOnly = auction.auctionDate.split('T')[0];
        const [year, month, day] = dateOnly.split("-").map(Number);
        const [hour, minute] = auction.auctionStartTime.split(":").map(Number);
        return new Date(year, month - 1, day, hour, minute, 0);
      } else {
        // Standard format: "2025-10-07"
        const [year, month, day] = auction.auctionDate.split("-").map(Number);
        const [hour, minute] = auction.auctionStartTime.split(":").map(Number);
        return new Date(year, month - 1, day, hour, minute, 0);
      }
    } catch (error) {
      console.warn("[getAuctionStart] Error parsing date/time:", auction.auctionDate, auction.auctionStartTime, error);
      return new Date();
    }
  };

  const formatAuctionDate = (dateStr: string) => {
    try {
      // Handle both ISO date format and YYYY-MM-DD format from backend
      let year: number, month: number, day: number;
      
      if (dateStr.includes('T')) {
        // ISO format: "2025-10-06T18:30:00.000Z" - extract date part only
        const dateOnly = dateStr.split('T')[0];
        [year, month, day] = dateOnly.split("-").map(Number);
      } else {
        // Standard format: "2025-10-07"
        [year, month, day] = dateStr.split("-").map(Number);
      }
      
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "dd-MM-yyyy");
    } catch (error) {
      console.warn("[formatAuctionDate] Error parsing date:", dateStr, error);
      // Fallback to original format
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [y, m, d] = datePart.split("-");
      return `${d}-${m}-${y}`;
    }
  };

  const formatAuctionTime = (time24: string) => {
    try {
      // Use date-fns for consistent time formatting
      const parsed = parse(time24, "HH:mm", new Date());
      if (isValid(parsed)) {
        return format(parsed, "hh:mm a");
      }
      // Fallback to manual formatting
      const [h, minute] = time24.split(":").map(Number);
      const suffix = h >= 12 ? "PM" : "AM";
      const hour12 = h % 12 || 12;
      return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
    } catch (error) {
      console.warn("[formatAuctionTime] Error parsing time:", time24, error);
      const [h, minute] = time24.split(":").map(Number);
      const suffix = h >= 12 ? "PM" : "AM";
      const hour12 = h % 12 || 12;
      return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
    }
  };

  const getAuctionEnd = (auction: BaseAuction) => {
    const start = getAuctionStart(auction).getTime();
    let durationInMinutes = auction.duration || 60;

    if (durationInMinutes > 1440) {
      durationInMinutes = Math.floor(durationInMinutes / 60);
    }

    const durMs = durationInMinutes * 60 * 1000;
    return new Date(start + durMs);
  };

  const getDerivedStatus = (
    auction: BaseAuction,
    nowMs: number
  ): BaseAuction["status"] => {
    if (auction.status === "completed") return "completed";

    const start = getAuctionStart(auction).getTime();
    const end = getAuctionEnd(auction).getTime();

    if (nowMs < start) return "upcoming";
    if (nowMs >= end) return "completed";
    return "live";
  };

  const formatCountdown = (ms: number) => {
    if (ms <= 0) return "00d 00h 00m 00s";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(days).padStart(2, "0")}d ${String(hours).padStart(
      2,
      "0"
    )}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
      2,
      "0"
    )}s`;
  };

  const remainingToStart = (auction: BaseAuction) => {
    return getAuctionStart(auction).getTime() - now;
  };

  const remainingToEnd = (auction: BaseAuction) => {
    return getAuctionEnd(auction).getTime() - now;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "live":
        return <Play className="w-4 h-4" />;
      case "upcoming":
        return <Clock className="w-4 h-4" />;
      case "completed":
        return <Calendar className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  // Filter auctions locally
  const filterAuctions = (auctions: BaseAuction[]) => {
    const term = searchTerm.trim().toLowerCase();
    const nowMs = now;
    return auctions
      .filter((auction) => {
        const derivedStatus = getDerivedStatus(auction, nowMs);
        const matchesSearch =
          term === "" ||
          auction.title.toLowerCase().includes(term) ||
          auction.auctionNo.toLowerCase().includes(term) ||
          (auction.auctioneerCompany?.toLowerCase().includes(term) ?? false);
        const matchesStatus =
          statusFilter === "all" || derivedStatus === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort(
        (a, b) => getAuctionStart(b).getTime() - getAuctionStart(a).getTime()
      );
  };

  const filteredAuctions = filterAuctions(auctions);

  // See MyCreatedA.tsx for why the skeleton is rendered inline below, inside
  // the normal header/filters chrome and the real .ap-myauctions-grid,
  // instead of replacing the whole page.

  return (
    <div className="ap-myauctions-wrapper">
      <div className="ap-myauctions-header">
        <div className="ap-myauctions-header-content">
          <div className="ap-myauctions-title-section">
            <h1 className="ap-myauctions-title">
              <Users className="w-8 h-8" />
              My Participated Auctions
            </h1>
            <p className="ap-myauctions-subtitle">
              Track auctions you're participating in
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="ap-myauctions-filters">
        <div className="ap-myauctions-filters-content">
          <div className="ap-myauctions-search-container">
            <div className="ap-myauctions-search-input-wrapper">
              <Search className="ap-myauctions-search-icon" />
              <input
                type="text"
                placeholder="Search auctions..."
                className="ap-myauctions-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="ap-myauctions-filter-controls">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="ap-myauctions-filter-select"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
            </select>

            <button
              className={`ap-myauctions-filter-btn ${
                isFetching ? "opacity-75" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={isFetching}
              title={isFetching ? "Refreshing data" : "Click to refresh"}
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
              <span className="ml-1">
                {isFetching ? "Refreshing" : "Refresh"}
              </span>
            </button>
          </div>
        </div>
        {apiError && (
          <div className="ap-myauctions-error-banner">
            <span>{apiError}</span>
            <button onClick={() => fetchAuctions()}>Retry</button>
          </div>
        )}
      </div>

      {/* Auctions List */}
      <div className="ap-myauctions-content">
        {isFetching && !loading && (
          <div className="ap-myauctions-loading-inline">
            Loading latest auctions...
          </div>
        )}
        {loading ? (
          <div className="ap-myauctions-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="ap-myauctions-card ap-skel-card">
                <div className="ap-myauctions-card-header">
                  <div className="ap-myauctions-card-title-section">
                    <div className="ap-skel-line ap-skel-title" style={{ width: "65%" }} />
                    <div className="ap-skel-badge" />
                  </div>
                  <div className="ap-skel-line short" style={{ height: 10 }} />
                </div>
                <div className="ap-myauctions-card-info">
                  <div className="ap-myauctions-info-item">
                    <div className="ap-skel-line" style={{ width: "55%", height: 10 }} />
                    <div className="ap-skel-line" style={{ width: "80%" }} />
                  </div>
                  <div className="ap-myauctions-info-item">
                    <div className="ap-skel-line" style={{ width: "55%", height: 10 }} />
                    <div className="ap-skel-line" style={{ width: "40%" }} />
                  </div>
                </div>
                <div className="ap-myauctions-card-actions">
                  <div className="ap-skel-line" style={{ width: "100%", height: 38, borderRadius: 8, margin: 0 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredAuctions.length > 0 ? (
          <div className="ap-myauctions-grid">
            {filteredAuctions.map((auction) => {
              const derivedStatus = getDerivedStatus(auction, now);
              const startsInMs = remainingToStart(auction);
              const endsInMs = remainingToEnd(auction);

              return (
                <div key={auction.id} className="ap-myauctions-card">
                  <div className="ap-myauctions-card-header">
                    <div className="ap-myauctions-card-title-section">
                      <h3 className="ap-myauctions-card-title">
                        {auction.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span
                          className={`ap-myauctions-status-badge ap-myauctions-status-${derivedStatus}`}
                        >
                          {getStatusIcon(derivedStatus)}
                          {derivedStatus.toUpperCase()}
                        </span>

                      </div>
                    </div>
                    <p className="ap-myauctions-card-subtitle">
                      Auction No: {auction.auctionNo}
                    </p>
                  </div>

                  <div className="ap-myauctions-card-info">
                    <div className="ap-myauctions-info-item">
                      <span className="ap-myauctions-info-label">
                        Date & Time:
                      </span>
                      <p className="ap-myauctions-info-value">
                        <Calendar className="w-4 h-4" />
                        {formatAuctionDate(auction.auctionDate)} at{" "}
                        {formatAuctionTime(auction.auctionStartTime)}
                      </p>
                    </div>

                    {auction.auctioneerCompany && (
                      <div className="ap-myauctions-info-item">
                        <span className="ap-myauctions-info-label">
                          Auctioneer:
                        </span>
                        <p className="ap-myauctions-info-value">
                          <Gavel className="w-4 h-4" />
                          {auction.auctioneerCompany}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="ap-myauctions-card-meta">
                    {derivedStatus === "upcoming" && (
                      <div
                        className="ap-myauctions-countdown"
                        title="Time until auction starts"
                      >
                        Starts in: {formatCountdown(startsInMs)}
                      </div>
                    )}
                    {derivedStatus === "live" && (
                      <div
                        className="ap-myauctions-countdown"
                        title="Auction in progress (time remaining)"
                      >
                        Live • {formatCountdown(Math.max(endsInMs, 0))}
                      </div>
                    )}
                  </div>

                  <div className="ap-myauctions-card-actions">
                    {/* <Link
                      to={`/dashboard/auction/${
                        auction.backendId || auction.id
                      }`}
                      className="ap-myauctions-action-btn ap-myauctions-view-btn"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Link> */}
                    <Link
                      to={`/dashboard/auction/${
                        auction.backendId || auction.id
                      }`}
                      className="ap-myauctions-action-btn ap-myauctions-view-btn"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Link>

                    {derivedStatus === "live" && (
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!auction.backendId && !auction.id) {
                            console.error("No auction ID found");
                            return;
                          }
                          const targetId = auction.backendId || auction.id;
                          try {
                            const button = e.currentTarget as HTMLButtonElement;
                            if (button) {
                              button.disabled = true;
                              button.innerText = "Joining...";
                            }
                            const { default: newAuctionService } = await import(
                              "../../../services/newAuctionService"
                            );
                            const { joinParticipant } = await import(
                              "../../../services/apiAuctionService"
                            );

                            let joined = false;
                            try {
                              const numId = Number(targetId);
                              if (!isNaN(numId) && user?.phoneNumber) {
                                const result =
                                  await newAuctionService.joinAuction({
                                    auction_id: numId,
                                    phone_number: user.phoneNumber,
                                  });
                                joined = !!result?.success;
                              }
                            } catch (newErr) {
                              console.warn(
                                "New join API failed, trying legacy",
                                newErr
                              );
                            }

                            if (!joined) {
                              await joinParticipant(targetId);
                            }

                            navigate(
                              `/dashboard/participant-auction/${targetId}`
                            );
                          } catch (err: any) {
                            console.error("Failed to join auction:", err);
                            alert(err?.message || "Failed to join auction");
                          } finally {
                            const button = e.currentTarget as HTMLButtonElement;
                            if (button) {
                              button.disabled = false;
                              button.innerText = "Join as Participant";
                            }
                          }
                        }}
                        className="ap-myauctions-action-btn ap-myauctions-join-btn"
                      >
                        <Play className="w-4 h-4" />
                        Join as Participant
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ap-myauctions-empty">
            <div className="ap-myauctions-empty-content">
              <Users className="ap-myauctions-empty-icon" />
              <h3 className="ap-myauctions-empty-title">No auctions found</h3>
              <p className="ap-myauctions-empty-subtitle">
                You haven't participated in any auctions yet. You need to be
                added as a participant by the auctioneer to see auctions here.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyParticipatedAuctions;

